/**
 * charts.tsx — 纯 SVG 图表原语（移植自原型 charts.jsx + metrics-shared.jsx）
 *
 * 无外部依赖、数据驱动：Sparkline / AreaChart / BarChart / StackedBarChart /
 * Donut / Funnel / RetentionHeatmap / RealtimeLine / CountUp /
 * LineChartX（多折线·类目 X 轴·null 断点）。
 */

import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

type Num = number;
type Padding = { l: number; r: number; t: number; b: number };

// ---------- ResizeObserver 宽度 hook ----------
function useWidth(initial = 640): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(initial);
  useEffect(() => {
    const ro = new ResizeObserver((es) => {
      const first = es[0];
      if (first) setW(Math.max(120, first.contentRect.width));
    });
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// ---------- Sparkline ----------
export function Sparkline({
  data,
  height = 28,
  width = 96,
  color = 'var(--accent)',
  showFill = true,
  strokeWidth = 1.2,
}: {
  data: Array<number | { value: number }>;
  height?: number;
  width?: number;
  color?: string;
  showFill?: boolean;
  strokeWidth?: number;
}) {
  if (!data || !data.length) return null;
  const vs = data.map((d) => (typeof d === 'number' ? d : d.value));
  const min = Math.min(...vs);
  const max = Math.max(...vs);
  const range = max - min || 1;
  const pts = vs.map((v, i) => {
    const x = (i / (vs.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as [number, number];
  });
  const path = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {showFill && <path d={area} fill={color} opacity="0.10" />}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---------- Area chart (interactive) ----------
export interface AreaSeries {
  key: string;
  color: string;
  label?: string;
}
export function AreaChart({
  data,
  height = 220,
  series = [{ key: 'value', color: 'var(--chart-1)' }],
  xKey = 'date',
  yFormat = (v: Num) => v.toLocaleString(),
  showGrid = true,
  showXAxis = true,
  padding = { l: 36, r: 12, t: 12, b: 22 },
}: {
  data: Array<Record<string, Num | string>>;
  height?: number;
  series?: AreaSeries[];
  xKey?: string;
  yFormat?: (v: Num) => string;
  showGrid?: boolean;
  showXAxis?: boolean;
  padding?: Padding;
}) {
  const [wrapRef, w] = useWidth();
  const [hover, setHover] = useState<{ i: number; x: number } | null>(null);

  const { paths, ticks, xs } = useMemo(() => {
    const allVals = data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0));
    const max = Math.max(...allVals, 1);
    const min = 0;
    const innerW = w - padding.l - padding.r;
    const innerH = height - padding.t - padding.b;
    const xs = data.map((_, i) => padding.l + (i / (data.length - 1 || 1)) * innerW);
    const yScale = (v: number) => padding.t + innerH - ((v - min) / (max - min)) * innerH;
    const paths = series.map((s) => {
      const pts = data.map((d, i) => [xs[i], yScale(Number(d[s.key]) || 0)] as [number, number]);
      const line = pts
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
        .join(' ');
      const area = `${line} L ${xs[xs.length - 1]} ${padding.t + innerH} L ${xs[0]} ${padding.t + innerH} Z`;
      return { ...s, line, area, pts };
    });
    const tickCount = 4;
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
      const v = (max / tickCount) * i;
      return { v, y: yScale(v) };
    });
    return { paths, ticks, xs };
  }, [data, w, height, series, padding.l, padding.r, padding.t, padding.b]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let bestI = 0;
    let bestD = Number.POSITIVE_INFINITY;
    xs.forEach((xv, i) => {
      const d = Math.abs(xv - x);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    });
    const bx = xs[bestI];
    if (bx !== undefined) setHover({ i: bestI, x: bx });
  };

  const hoverRow = hover ? data[hover.i] : undefined;

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg
        width={w}
        height={height}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {showGrid &&
          ticks.map((t, i) => (
            <g key={i}>
              <line
                x1={padding.l}
                x2={w - padding.r}
                y1={t.y}
                y2={t.y}
                stroke="var(--border)"
                strokeDasharray={i === 0 ? '0' : '3 3'}
              />
              <text
                x={padding.l - 8}
                y={t.y + 3.5}
                textAnchor="end"
                fontSize="10"
                fill="var(--fg-faint)"
                fontFamily="var(--font-mono)"
              >
                {t.v >= 1000 ? `${(t.v / 1000).toFixed(t.v >= 10000 ? 0 : 1)}k` : t.v.toFixed(0)}
              </text>
            </g>
          ))}
        {paths.map((p, i) => (
          <path key={i} d={p.area} fill={p.color} opacity="0.10" />
        ))}
        {paths.map((p, i) => (
          <path
            key={i}
            d={p.line}
            fill="none"
            stroke={p.color}
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {showXAxis && data.length > 0 && (
          <g>
            {/* X 轴标签取首 / 中 / 末三个索引；data.length ≤ 2 时三者会塌缩重复（如 length=1 → [0,0,0]），
                必须去重，否则同 key 渲染重复子元素（React duplicate-key 报错 + 标签重叠）。 */}
            {[...new Set([0, Math.floor((data.length - 1) / 2), data.length - 1])].map((i) => {
              const row = data[i];
              const xv = xs[i];
              if (row === undefined || xv === undefined) return null;
              return (
                <text
                  key={i}
                  x={xv}
                  y={height - 4}
                  textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
                  fontSize="10"
                  fill="var(--fg-faint)"
                  fontFamily="var(--font-mono)"
                >
                  {String(row[xKey] || '').slice(5)}
                </text>
              );
            })}
          </g>
        )}
        {hover && (
          <g>
            <line
              x1={hover.x}
              x2={hover.x}
              y1={padding.t}
              y2={height - padding.b}
              stroke="var(--fg-faint)"
              strokeDasharray="2 2"
            />
            {paths.map((p, i) => {
              const pt = p.pts[hover.i];
              if (!pt) return null;
              return (
                <circle
                  key={i}
                  cx={pt[0]}
                  cy={pt[1]}
                  r="3.5"
                  fill="var(--bg-elev)"
                  stroke={p.color}
                  strokeWidth="1.6"
                />
              );
            })}
          </g>
        )}
      </svg>
      {hover && hoverRow && (
        <div
          className="tooltip"
          style={{ left: Math.min(w - 140, Math.max(0, hover.x + 8)), top: 6 }}
        >
          <div style={{ color: 'var(--fg-faint)', marginBottom: 2 }}>{hoverRow[xKey]}</div>
          {paths.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 6, height: 6, background: p.color, borderRadius: 999 }} />
              <span style={{ color: 'var(--fg-faint)' }}>{p.label || p.key}</span>
              <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                {yFormat(Number(hoverRow[p.key]))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Bar chart ----------
export interface BarDatum {
  label: string;
  value: number;
  color?: string;
}
export function BarChart({
  data,
  height = 200,
  color = 'var(--chart-1)',
  yFormat = (v: Num) => v.toLocaleString(),
  padding = { l: 36, r: 8, t: 12, b: 22 },
}: {
  data: BarDatum[];
  height?: number;
  color?: string;
  yFormat?: (v: Num) => string;
  padding?: Padding;
}) {
  const [wrapRef, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  const innerW = w - padding.l - padding.r;
  const innerH = height - padding.t - padding.b;
  const bw = innerW / data.length;
  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = padding.t + innerH * (1 - p);
          return (
            <g key={i}>
              <line
                x1={padding.l}
                x2={w - padding.r}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray={i === 0 ? '0' : '3 3'}
              />
              <text
                x={padding.l - 6}
                y={y + 3.5}
                textAnchor="end"
                fontSize="10"
                fill="var(--fg-faint)"
                fontFamily="var(--font-mono)"
              >
                {max * p >= 1000 ? `${((max * p) / 1000).toFixed(0)}k` : (max * p).toFixed(0)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = (d.value / max) * innerH;
          const x = padding.l + bw * i + bw * 0.18;
          const y = padding.t + innerH - h;
          const isHov = hover === i;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect
                x={padding.l + bw * i}
                y={padding.t}
                width={bw}
                height={innerH}
                fill="transparent"
              />
              <rect
                x={x}
                y={y}
                width={bw * 0.64}
                height={h}
                fill={d.color || color}
                opacity={hover === null || isHov ? 1 : 0.5}
                rx="2"
              />
              {data.length <= 12 && (
                <text
                  x={padding.l + bw * i + bw / 2}
                  y={height - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill="var(--fg-muted)"
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div className="tooltip" style={{ left: padding.l + bw * hover + bw * 0.5 - 50, top: 4 }}>
          <span style={{ color: 'var(--fg-faint)' }}>{data[hover]?.label}</span>{' '}
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {yFormat(data[hover]?.value ?? 0)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------- Stacked bar chart ----------
export function StackedBarChart({
  data,
  keys,
  colors,
  height = 220,
  padding = { l: 40, r: 12, t: 12, b: 22 },
}: {
  data: Array<Record<string, Num | string>>;
  keys: string[];
  colors: string[];
  height?: number;
  padding?: Padding;
}) {
  const [wrapRef, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const totals = data.map((d) => keys.reduce((s, k) => s + (Number(d[k]) || 0), 0));
  const max = Math.max(...totals, 1);
  const innerW = w - padding.l - padding.r;
  const innerH = height - padding.t - padding.b;
  const bw = innerW / data.length;
  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = padding.t + innerH * (1 - p);
          return (
            <g key={i}>
              <line
                x1={padding.l}
                x2={w - padding.r}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray={i === 0 ? '0' : '3 3'}
              />
              <text
                x={padding.l - 6}
                y={y + 3.5}
                textAnchor="end"
                fontSize="10"
                fill="var(--fg-faint)"
                fontFamily="var(--font-mono)"
              >
                {((max * p) / 1000).toFixed(0)}k
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          let yOff = padding.t + innerH;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect
                x={padding.l + bw * i}
                y={padding.t}
                width={bw}
                height={innerH}
                fill="transparent"
              />
              {keys.map((k, ki) => {
                const v = Number(d[k]) || 0;
                const h = (v / max) * innerH;
                yOff -= h;
                return (
                  <rect
                    key={k}
                    x={padding.l + bw * i + bw * 0.18}
                    y={yOff}
                    width={bw * 0.64}
                    height={h}
                    fill={colors[ki]}
                    opacity={hover === null || hover === i ? 1 : 0.5}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div
          className="tooltip"
          style={{
            left: Math.min(w - 180, padding.l + bw * hover + bw),
            top: 6,
            padding: '6px 9px',
          }}
        >
          <div style={{ color: 'var(--fg-faint)', marginBottom: 3 }}>{data[hover]?.date}</div>
          {keys.map((k, ki) => (
            <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 140 }}>
              <span style={{ width: 6, height: 6, background: colors[ki], borderRadius: 999 }} />
              <span style={{ color: 'var(--fg-faint)' }}>{k}</span>
              <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                €{Math.round(Number(data[hover]?.[k])).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Donut / Ring ----------
export function Donut({
  data,
  size = 130,
  thickness = 14,
  gap = 0.02,
}: {
  data: Array<{ value: number; color: string }>;
  size?: number;
  thickness?: number;
  gap?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = size / 2 - thickness / 2;
  const cx = size / 2;
  const cy = size / 2;
  let acc = -Math.PI / 2;
  const arcs = data.map((d) => {
    const angle = (d.value / total) * (2 * Math.PI) - gap;
    const start = acc;
    const end = acc + angle;
    acc = end + gap;
    const x1 = cx + Math.cos(start) * r;
    const y1 = cy + Math.sin(start) * r;
    const x2 = cx + Math.cos(end) * r;
    const y2 = cy + Math.sin(end) * r;
    const large = angle > Math.PI ? 1 : 0;
    return { ...d, path: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}` };
  });
  return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-muted)" strokeWidth={thickness} />
      {arcs.map((a, i) => (
        <path
          key={i}
          d={a.path}
          fill="none"
          stroke={a.color}
          strokeWidth={thickness}
          strokeLinecap="butt"
        />
      ))}
    </svg>
  );
}

// ---------- Funnel ----------
export function Funnel({
  data,
}: { data: Array<{ label: string; value: number; color?: string }> }) {
  const top = data[0]?.value ?? 1;
  return (
    <div className="vstack" style={{ gap: 4 }}>
      {data.map((d, i) => {
        const pct = (d.value / top) * 100;
        const prev = data[i - 1];
        // 上一级为 0 时转化率无定义 → 显「—」，不渲染 NaN%
        const conv =
          i === 0 || !prev
            ? null
            : prev.value > 0
              ? ((d.value / prev.value) * 100).toFixed(1)
              : null;
        const convUndefined = i > 0 && !!prev && prev.value <= 0;
        return (
          <div key={i} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div className="hstack" style={{ gap: 8 }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--bg-subtle)',
                    border: '1px solid var(--border)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--fg-muted)',
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg)' }}>{d.label}</span>
              </div>
              <div className="hstack mono" style={{ fontSize: 11, gap: 10 }}>
                <span className="strong">{d.value.toLocaleString()}</span>
                {conv && <span className={Number(conv) > 50 ? 'delta-pos' : 'muted'}>{conv}%</span>}
                {convUndefined && <span className="muted">—</span>}
              </div>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 3,
                background: 'var(--bg-muted)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, var(--chart-1), var(--chart-2))',
                  opacity: 1 - i * 0.07,
                  borderRadius: 3,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Retention heatmap ----------
export function RetentionHeatmap({ data }: { data: Array<{ cohort: string; days: number[] }> }) {
  return (
    <div style={{ overflow: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 11 }}>
        <thead>
          <tr>
            <th
              style={{
                padding: '4px 8px',
                color: 'var(--fg-muted)',
                fontWeight: 500,
                textAlign: 'left',
              }}
            >
              Cohort
            </th>
            {(data[0]?.days ?? []).map((_, i) => (
              <th
                key={i}
                style={{
                  padding: '4px 6px',
                  color: 'var(--fg-muted)',
                  fontWeight: 500,
                  textAlign: 'center',
                  minWidth: 36,
                }}
                className="mono"
              >
                D{i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              <td className="mono" style={{ padding: '2px 8px', color: 'var(--fg-muted)' }}>
                {row.cohort.slice(5)}
              </td>
              {row.days.map((v, j) => {
                const intensity = v / 100;
                const bg =
                  j === 0
                    ? 'var(--fg-strong)'
                    : `color-mix(in oklab, var(--accent) ${intensity * 90}%, var(--bg-muted))`;
                const fg = intensity > 0.45 || j === 0 ? 'white' : 'var(--fg)';
                return (
                  <td
                    key={j}
                    style={{
                      padding: 0,
                      background: bg,
                      color: fg,
                      textAlign: 'center',
                      height: 24,
                      borderRadius: 3,
                      minWidth: 36,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      fontWeight: j === 0 ? 600 : 400,
                    }}
                  >
                    {v}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Realtime line ----------
export function RealtimeLine({
  data,
  height = 100,
  color = 'var(--chart-2)',
}: {
  data: Array<{ online: number }>;
  height?: number;
  color?: string;
}) {
  const [wrapRef, w] = useWidth(360);
  const max = Math.max(...data.map((d) => d.online));
  const min = Math.min(...data.map((d) => d.online));
  const range = max - min || 1;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - ((d.online - min) / range) * (height - 12) - 6;
    return [x, y] as [number, number];
  });
  const path = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');
  const area = `${path} L ${w} ${height} L 0 ${height} Z`;
  const last = pts[pts.length - 1];
  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        <path d={area} fill={color} opacity="0.10" />
        <path d={path} fill="none" stroke={color} strokeWidth="1.6" />
        {last && (
          <>
            <circle cx={last[0]} cy={last[1]} r="4" fill={color} className="pulse-dot" />
            <circle cx={last[0]} cy={last[1]} r="2.5" fill="white" />
          </>
        )}
      </svg>
    </div>
  );
}

// ---------- CountUp ----------
export function CountUp({
  value,
  format = (v: Num) => v.toLocaleString(),
  duration = 700,
  decimals = 0,
}: {
  value: number;
  format?: (v: Num) => string;
  duration?: number;
  /** 保留小数位（LTV/CPI 比值等非整数指标；0 = 取整，与既有行为一致） */
  decimals?: number;
}): ReactNode {
  const [v, setV] = useState(0);
  const ref = useRef(value);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 仅在目标 value 变化时重新动画
  useEffect(() => {
    ref.current = value;
    const startV = v;
    // 起点取首帧时间戳：帧时间戳与 performance.now() 不保证同源（浏览器首帧早于调用时刻、jsdom 原点不同），
    // 混用会让进度为负、逐帧显示负数并拖慢到达终值。
    let start: number | undefined;
    let raf: number;
    const step = (now: number) => {
      start ??= now;
      // duration ≤ 0 视为不做动画：直接到终值，避免首帧 0/0 得 NaN 后停在 NaN。
      const t = duration > 0 ? Math.min(1, (now - start) / duration) : 1;
      const eased = 1 - (1 - t) ** 3;
      setV(startV + (ref.current - startV) * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  // 逐帧值按 decimals 量化再交给 format：decimals=0 保持原取整行为；>0 时 1.35 不再塌成 1
  const factor = 10 ** decimals;
  return <>{format(Math.round(v * factor) / factor)}</>;
}

// ---------- 多折线图：类目 X 轴 · null 断点 · hover 十字线 + legend（移植自原型 metrics-shared.jsx）----------

export interface LineChartXSeries {
  label: string;
  color: string;
  /** 逐类目值；null = 无数据日（断线不补零，段间重新起笔） */
  values: Array<number | null>;
}

export function LineChartX({
  series,
  xLabels,
  height = 220,
  yFormat = (v: Num) => String(v),
  yMax,
  allowNegative = false,
  padding = { l: 44, r: 14, t: 14, b: 22 },
  showLegend = true,
}: {
  series: LineChartXSeries[];
  xLabels: string[];
  height?: number;
  yFormat?: (v: Num) => string;
  yMax?: number;
  /** 收入冲正等有符号指标可显式启用负值范围；默认保持零基线。 */
  allowNegative?: boolean;
  padding?: Padding;
  showLegend?: boolean;
}) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const innerW = w - padding.l - padding.r;
  const innerH = height - padding.t - padding.b;
  const allVals = series.flatMap((s) => s.values.filter((v): v is number => v != null));
  const max = (yMax ?? Math.max(...allVals, 0.001)) * 1.08;
  const min = allowNegative ? Math.min(0, ...allVals) * 1.08 : 0;
  const xs = xLabels.map((_, i) => padding.l + (i / (xLabels.length - 1 || 1)) * innerW);
  const yS = (v: number) => padding.t + innerH - ((v - min) / (max - min)) * innerH;
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let bi = 0;
    let bd = Number.POSITIVE_INFINITY;
    xs.forEach((xv, i) => {
      const d = Math.abs(xv - x);
      if (d < bd) {
        bd = d;
        bi = i;
      }
    });
    setHover(bi);
  };
  const hoverX = hover != null ? xs[hover] : undefined;
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <svg
        width={w}
        height={height}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label="多折线图"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = padding.t + innerH * (1 - p);
          return (
            <g key={p}>
              <line
                x1={padding.l}
                x2={w - padding.r}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray={i === 0 ? '0' : '3 3'}
              />
              <text
                x={padding.l - 8}
                y={y + 3.5}
                textAnchor="end"
                fontSize="10"
                fill="var(--fg-faint)"
                fontFamily="var(--font-mono)"
              >
                {yFormat(min + (max - min) * p)}
              </text>
            </g>
          );
        })}
        {series.map((s, si) => {
          // null 值断线：段间重新 M 起笔（无数据日不补零、不连线）
          let path = '';
          let started = false;
          s.values.forEach((v, i) => {
            const xv = xs[i];
            if (v == null || xv === undefined) {
              started = false;
              return;
            }
            path += `${started ? 'L' : 'M'} ${xv.toFixed(1)} ${yS(v).toFixed(1)} `;
            started = true;
          });
          return (
            <Fragment key={si}>
              <path
                d={path}
                fill="none"
                stroke={s.color}
                strokeWidth="1.7"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {s.values.map((value, index) => {
                const x = xs[index];
                if (value == null || x === undefined) return null;
                const hasPreviousPoint = s.values[index - 1] != null && xs[index - 1] !== undefined;
                const hasNextPoint = s.values[index + 1] != null && xs[index + 1] !== undefined;
                if (hasPreviousPoint || hasNextPoint) return null;
                // 单点段只有 M，没有可见线段；复用悬停点标记区分 READY 零值与缺失日。
                return (
                  <circle
                    key={index}
                    cx={x}
                    cy={yS(value)}
                    r="3.2"
                    fill="var(--bg-elev)"
                    stroke={s.color}
                    strokeWidth="1.6"
                  />
                );
              })}
            </Fragment>
          );
        })}
        {xLabels.map((l, i) => {
          // 类目多时抽稀 X 轴标签（≤12 全显，否则约 8 个）
          if (!(xLabels.length <= 12 || i % Math.ceil(xLabels.length / 8) === 0)) return null;
          return (
            <text
              key={i}
              x={xs[i]}
              y={height - 4}
              textAnchor="middle"
              fontSize="10"
              fill="var(--fg-faint)"
              fontFamily="var(--font-mono)"
            >
              {l}
            </text>
          );
        })}
        {hover != null && hoverX !== undefined && (
          <g>
            <line
              x1={hoverX}
              x2={hoverX}
              y1={padding.t}
              y2={height - padding.b}
              stroke="var(--fg-faint)"
              strokeDasharray="2 2"
            />
            {series.map((s, si) => {
              const v = s.values[hover];
              if (v == null) return null;
              return (
                <circle
                  key={si}
                  cx={hoverX}
                  cy={yS(v)}
                  r="3.2"
                  fill="var(--bg-elev)"
                  stroke={s.color}
                  strokeWidth="1.6"
                />
              );
            })}
          </g>
        )}
      </svg>
      {hover != null && hoverX !== undefined && (
        <div
          className="tooltip"
          style={{ left: Math.min(w - 170, Math.max(0, hoverX + 8)), top: 4, minWidth: 150 }}
        >
          <div style={{ color: 'var(--fg-faint)', marginBottom: 2 }}>{xLabels[hover]}</div>
          {series.map((s, si) => {
            const v = s.values[hover];
            return (
              <div key={si} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ width: 6, height: 6, background: s.color, borderRadius: 999 }} />
                <span style={{ color: 'var(--fg-faint)' }}>{s.label}</span>
                <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                  {v == null ? '—' : yFormat(v)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {showLegend && (
        <div className="hstack" style={{ gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {series.map((s) => (
            <span
              key={s.label}
              className="hstack"
              style={{ gap: 5, fontSize: 11, color: 'var(--fg-muted)' }}
            >
              <span style={{ width: 10, height: 2, background: s.color, borderRadius: 2 }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** 命名空间导出，保持原型 `Charts.AreaChart` 调用风格 */
export const Charts = {
  Sparkline,
  AreaChart,
  BarChart,
  StackedBarChart,
  Donut,
  Funnel,
  RetentionHeatmap,
  RealtimeLine,
  CountUp,
  LineChartX,
} as const;
